import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Row, Col, Form, Button, Alert, Card } from 'react-bootstrap';
import authService from '../services/authService';
import businessService from '../services/businessService';
import BackToDashboard from '../components/BackToDashboard';

function CoreProductPricing() {
  const navigate = useNavigate();
  const [stage, setStage] = useState('review'); // review, pricing_model, price_input
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  // Core product data
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [isEditingProduct, setIsEditingProduct] = useState(false);

  // Pricing model data
  const [pricingModel, setPricingModel] = useState(''); // monthly, annual, quarterly, one-time, hybrid
  const [billingFrequency, setBillingFrequency] = useState('');

  // Pricing data
  const [monthlyPrice, setMonthlyPrice] = useState('');
  const [annualPrice, setAnnualPrice] = useState('');
  const [quarterlyPrice, setQuarterlyPrice] = useState('');
  const [oneTimePrice, setOneTimePrice] = useState('');
  const [offerAnnual, setOfferAnnual] = useState(false);
  const [annualDiscount, setAnnualDiscount] = useState('');
  const [offerQuarterly, setOfferQuarterly] = useState(false);
  const [quarterlyDiscount, setQuarterlyDiscount] = useState('');
  const [costToDeliver, setCostToDeliver] = useState('');
  const [hybridOneTime, setHybridOneTime] = useState('');
  const [hybridMonthly, setHybridMonthly] = useState('');
  const [hybridOneTimeCost, setHybridOneTimeCost] = useState('');
  const [hybridMonthlyCost, setHybridMonthlyCost] = useState('');

  // Hourly pricing
  const [hourlyRate, setHourlyRate] = useState('');
  const [typicalHoursMin, setTypicalHoursMin] = useState('');
  const [typicalHoursMax, setTypicalHoursMax] = useState('');
  const [hoursVarySignificantly, setHoursVarySignificantly] = useState(false);
  const [hourlyCostPerHour, setHourlyCostPerHour] = useState('');

  // Reassurance banner
  const [showReassurance, setShowReassurance] = useState(
    !localStorage.getItem('hideReassuranceBanner')
  );

  const dismissReassurance = () => {
    localStorage.setItem('hideReassuranceBanner', 'true');
    setShowReassurance(false);
  };

  useEffect(() => {
    checkAuthAndLoad();
  }, []);

  const checkAuthAndLoad = async () => {
    // Check authentication
    const authResult = await authService.checkAuth();
    if (!authResult.authenticated) {
      navigate('/login');
      return;
    }

    // Load core product from Business Basics
    const result = await businessService.getFieldsByNames([
      'core_product',
      'business_name',
      'pricing_data'
    ]);

    let coreProductDescription = '';  // From business field (stores description)
    let coreProductName = '';         // Actual product name
    let businessName = '';
    let foundProductFromTable = false;

    if (result.success && result.data.fields) {
      coreProductDescription = result.data.fields.core_product || '';
      businessName = result.data.fields.business_name || '';
    }

    // Also check Product table for quick-start users
    // If no core_product in business fields, look for core product in Product table
    try {
      // Fetch both products and upgrade paths (like FunnelDiagram does)
      const [productsResponse, funnelResponse] = await Promise.all([
        fetch('/api/products/get-catalog', { method: 'GET', credentials: 'include' }),
        fetch('/api/marketing/funnel-diagram', { method: 'GET', credentials: 'include' })
      ]);

      let products = [];
      let upgradePaths = [];

      if (productsResponse.ok) {
        const productsData = await productsResponse.json();
        products = productsData.catalog || productsData.products || [];
      }

      if (funnelResponse.ok) {
        const funnelData = await funnelResponse.json();
        upgradePaths = funnelData.upgrade_paths || [];
      }

      if (products.length > 0) {
        // Debug: log products to see positions
        console.log('CoreProductPricing - Products from catalog:', products.map(p => ({
          name: p.name,
          position: p.position,
          is_primary_tripwire: p.is_primary_tripwire,
          price: p.sale_price
        })));
        console.log('CoreProductPricing - Upgrade paths:', upgradePaths);

        // Find core product - look for position='core' (case-insensitive)
        let coreFromTable = products.find(p =>
          p.position && p.position.toLowerCase() === 'core'
        );

        // If no explicit position, infer from upgrade paths (like FunnelDiagram does)
        if (!coreFromTable && upgradePaths.length > 0) {
          const sourceIds = new Set(upgradePaths.map(p => p.from_product_id));
          const targetIds = new Set(upgradePaths.map(p => p.to_product_id));

          // Core = product that is BOTH source AND target (middle of upgrade chain)
          coreFromTable = products.find(p =>
            sourceIds.has(p.id) && targetIds.has(p.id)
          );

          // If no middle product, core = target that isn't tripwire (upgrade destination)
          if (!coreFromTable) {
            coreFromTable = products.find(p =>
              targetIds.has(p.id) && !p.is_primary_tripwire
            );
          }
        }

        // Fallback: cheapest non-tripwire, non-premium product
        if (!coreFromTable) {
          const coreProducts = products.filter(p =>
            !p.is_primary_tripwire &&
            (!p.position || (p.position.toLowerCase() !== 'tripwire' && p.position.toLowerCase() !== 'premium'))
          );
          if (coreProducts.length > 0) {
            coreFromTable = coreProducts.reduce((min, p) =>
              (parseFloat(p.sale_price) || 0) < (parseFloat(min.sale_price) || 0) ? p : min
            , coreProducts[0]);
          } else {
            coreFromTable = products[0];
          }
        }

        console.log('CoreProductPricing - Selected core product:', coreFromTable?.name, 'position:', coreFromTable?.position);

        if (coreFromTable) {
          // Always use Product table data when products exist
          coreProductName = coreFromTable.name;
          foundProductFromTable = true;

          // Also set pricing data from product
          const isRecurring = coreFromTable.purchase_type && coreFromTable.purchase_type.includes('recurring');
          const pricingModel = isRecurring ? 'monthly' : 'one-time';
          setPricingModel(pricingModel);
          if (pricingModel === 'one-time') {
            setOneTimePrice(coreFromTable.sale_price || '');
          } else {
            setMonthlyPrice(coreFromTable.sale_price || '');
          }
          setCostToDeliver(coreFromTable.cost_to_deliver || '');
          setStage('price_input');
        }
      }
    } catch (err) {
      console.error('Error loading products:', err);
    }

    // Set product name and description
    // If we found from Product table, use that name; otherwise fall back to business name
    setProductName(coreProductName || businessName);
    // Description: use core_product field if it exists and is different from the name, otherwise leave empty
    setProductDescription(foundProductFromTable ? '' : coreProductDescription);

    // Load existing pricing data if it exists (only if we didn't already load from Product table)
    if (!foundProductFromTable && result.success && result.data.fields) {
      const pricingDataStr = result.data.fields.pricing_data;
      if (pricingDataStr) {
        try {
          const pricingData = JSON.parse(pricingDataStr);

          // Populate all pricing fields
          setPricingModel(pricingData.pricing_model || '');
          setBillingFrequency(pricingData.billing_frequency || '');
          setMonthlyPrice(pricingData.monthly_price || '');
          setAnnualPrice(pricingData.annual_price || '');
          setQuarterlyPrice(pricingData.quarterly_price || '');
          setOneTimePrice(pricingData.one_time_price || '');
          setOfferAnnual(pricingData.offer_annual || false);
          setAnnualDiscount(pricingData.annual_discount || '');
          setOfferQuarterly(pricingData.offer_quarterly || false);
          setQuarterlyDiscount(pricingData.quarterly_discount || '');
          setCostToDeliver(pricingData.cost_to_deliver || '');
          setHybridOneTime(pricingData.hybrid_one_time || '');
          setHybridMonthly(pricingData.hybrid_monthly || '');
          setHybridOneTimeCost(pricingData.hybrid_one_time_cost || '');
          setHybridMonthlyCost(pricingData.hybrid_monthly_cost || '');
          setHourlyRate(pricingData.hourly_rate || '');
          setTypicalHoursMin(pricingData.typical_hours_min || '');
          setTypicalHoursMax(pricingData.typical_hours_max || '');
          setHoursVarySignificantly(pricingData.hours_vary_significantly || false);
          setHourlyCostPerHour(pricingData.hourly_cost_per_hour || '');

          // If pricing data exists, skip to the price_input stage
          if (pricingData.pricing_model) {
            setStage('price_input');
          }
        } catch (error) {
          console.error('Error parsing pricing data:', error);
        }
      }
    }

    setLoading(false);
  };

  const handleProductEdit = async () => {
    if (!productName.trim() || !productDescription.trim()) {
      setError('Product name and description are required');
      return;
    }

    setSaving(true);
    setError('');

    // Save updated core product
    const result = await businessService.saveField('core_product', productDescription);

    setSaving(false);

    if (result.success) {
      setIsEditingProduct(false);
      setStage('pricing_model');
    } else {
      setError(result.error);
    }
  };

  const handleContinueFromReview = () => {
    setStage('pricing_model');
  };

  const handlePricingModelSelect = () => {
    if (!pricingModel) {
      setError('Please select a pricing model');
      return;
    }

    setError('');

    // Set billing frequency based on model
    if (pricingModel === 'monthly') {
      setBillingFrequency('monthly');
    } else if (pricingModel === 'annual') {
      setBillingFrequency('annual');
    } else if (pricingModel === 'quarterly') {
      setBillingFrequency('quarterly');
    } else if (pricingModel === 'one-time') {
      setBillingFrequency('one-time');
    } else if (pricingModel === 'hybrid') {
      setBillingFrequency('hybrid');
    } else if (pricingModel === 'hourly') {
      setBillingFrequency('hourly');
    }

    setStage('price_input');
  };

  const calculateProfit = () => {
    if (pricingModel === 'one-time') {
      const price = parseFloat(oneTimePrice) || 0;
      const cost = parseFloat(costToDeliver) || 0;
      return (price - cost).toFixed(2);
    } else if (pricingModel === 'hybrid') {
      const oneTime = parseFloat(hybridOneTime) || 0;
      const monthly = parseFloat(hybridMonthly) || 0;
      const oneTimeCost = parseFloat(hybridOneTimeCost) || 0;
      const monthlyCost = parseFloat(hybridMonthlyCost) || 0;
      const firstYearRevenue = oneTime + (monthly * 12);
      const firstYearCost = oneTimeCost + (monthlyCost * 12);
      return (firstYearRevenue - firstYearCost).toFixed(2);
    } else if (pricingModel === 'hourly') {
      const rate = parseFloat(hourlyRate) || 0;
      const costPerHour = parseFloat(hourlyCostPerHour) || 0;
      return (rate - costPerHour).toFixed(2);
    } else {
      // Recurring
      const price = parseFloat(pricingModel === 'monthly' ? monthlyPrice :
                               pricingModel === 'annual' ? annualPrice : quarterlyPrice) || 0;
      const cost = parseFloat(costToDeliver) || 0;
      return (price - cost).toFixed(2);
    }
  };

  const calculateTypicalProjectValue = () => {
    const rate = parseFloat(hourlyRate) || 0;
    const minHours = parseFloat(typicalHoursMin) || 0;
    const maxHours = parseFloat(typicalHoursMax) || 0;
    if (minHours && maxHours) {
      const avgHours = (minHours + maxHours) / 2;
      return {
        min: (rate * minHours).toFixed(0),
        max: (rate * maxHours).toFixed(0),
        avg: (rate * avgHours).toFixed(0)
      };
    }
    return null;
  };

  const handleSavePricing = async () => {
    // Validation
    if (pricingModel === 'one-time' && (!oneTimePrice || !costToDeliver)) {
      setError('Please enter one-time price and cost to deliver');
      return;
    } else if (pricingModel === 'hybrid' && (!hybridOneTime || !hybridMonthly || !hybridOneTimeCost || !hybridMonthlyCost)) {
      setError('Please enter all hybrid pricing details');
      return;
    } else if (pricingModel === 'hourly') {
      if (!hourlyRate) {
        setError('Please enter your hourly rate');
        return;
      }
      if (!hoursVarySignificantly && (!typicalHoursMin || !typicalHoursMax)) {
        setError('Please enter typical hours range or check "Hours vary significantly"');
        return;
      }
    } else if (['monthly', 'annual', 'quarterly'].includes(pricingModel)) {
      const mainPrice = pricingModel === 'monthly' ? monthlyPrice :
                        pricingModel === 'annual' ? annualPrice : quarterlyPrice;
      if (!mainPrice || !costToDeliver) {
        setError('Please enter pricing and cost to deliver');
        return;
      }
    }

    setSaving(true);
    setError('');

    // Determine purchase_type for Product table
    // Use recurring-monthly or recurring-annual to match the database schema
    let purchaseType = 'one-time';
    if (pricingModel === 'monthly' || pricingModel === 'hybrid') {
      purchaseType = 'recurring-monthly';
    } else if (pricingModel === 'annual') {
      purchaseType = 'recurring-annual';
    } else if (pricingModel === 'quarterly') {
      purchaseType = 'recurring-monthly';  // Treat quarterly as monthly for simplicity
    }

    // Determine price and cost based on model
    let salePrice = 0;
    let costValue = parseFloat(costToDeliver) || 0;

    if (pricingModel === 'one-time') {
      salePrice = parseFloat(oneTimePrice) || 0;
    } else if (pricingModel === 'monthly') {
      salePrice = parseFloat(monthlyPrice) || 0;
    } else if (pricingModel === 'annual') {
      salePrice = parseFloat(annualPrice) || 0;
    } else if (pricingModel === 'quarterly') {
      salePrice = parseFloat(quarterlyPrice) || 0;
    } else if (pricingModel === 'hybrid') {
      salePrice = parseFloat(hybridMonthly) || 0;  // Use monthly component as the recurring price
      costValue = parseFloat(hybridMonthlyCost) || 0;
    } else if (pricingModel === 'hourly') {
      salePrice = parseFloat(hourlyRate) || 0;
      costValue = parseFloat(hourlyCostPerHour) || 0;
    }

    // Prepare data to save
    const pricingData = {
      pricing_model: pricingModel,
      billing_frequency: billingFrequency,
      is_recurring: pricingModel !== 'one-time' && pricingModel !== 'hourly',
      monthly_price: monthlyPrice,
      annual_price: annualPrice,
      quarterly_price: quarterlyPrice,
      one_time_price: oneTimePrice,
      offer_annual: offerAnnual,
      annual_discount: annualDiscount,
      offer_quarterly: offerQuarterly,
      quarterly_discount: quarterlyDiscount,
      cost_to_deliver: costToDeliver,
      hybrid_one_time: hybridOneTime,
      hybrid_monthly: hybridMonthly,
      hybrid_one_time_cost: hybridOneTimeCost,
      hybrid_monthly_cost: hybridMonthlyCost,
      hourly_rate: hourlyRate,
      typical_hours_min: typicalHoursMin,
      typical_hours_max: typicalHoursMax,
      hours_vary_significantly: hoursVarySignificantly,
      hourly_cost_per_hour: hourlyCostPerHour,
      profit_per_sale: calculateProfit()
    };

    // Save pricing data to business_data
    const result = await businessService.saveField('pricing_data', JSON.stringify(pricingData));

    if (!result.success) {
      setSaving(false);
      setError(result.error);
      return;
    }

    // Also update the Product table with the purchase_type and pricing
    try {
      const catalogResponse = await fetch('/api/products/get-catalog', {
        method: 'GET',
        credentials: 'include'
      });

      if (catalogResponse.ok) {
        const catalogData = await catalogResponse.json();
        const products = catalogData.catalog || [];

        // Find core product (same logic as loading)
        let coreProduct = products.find(p => p.position && p.position.toLowerCase() === 'core');
        if (!coreProduct && products.length > 0) {
          coreProduct = products[0];  // Fallback to first product
        }

        if (coreProduct) {
          // Update the product with new pricing info
          const updatedProducts = products.map(p => {
            if (p.id === coreProduct.id) {
              return {
                ...p,
                purchase_type: purchaseType,
                sale_price: salePrice,
                cost_to_deliver: costValue,
                profit_per_sale: salePrice - costValue
              };
            }
            return p;
          });

          // Save updated catalog
          await fetch('/api/products/save-catalog', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              catalog: updatedProducts,
              all_products: updatedProducts
            })
          });
        }
      }
    } catch (err) {
      console.error('Error updating product table:', err);
      // Don't block navigation - pricing data was saved successfully
    }

    setSaving(false);

    // Check if one-time purchase to trigger recurring revenue workshop
    // Hourly is also non-recurring but doesn't need the workshop
    if (pricingModel === 'one-time') {
      navigate('/recurring-revenue-workshop');
    } else {
      navigate('/customer-experience');
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <p>Loading...</p>
      </Container>
    );
  }

  // STAGE 1: Review Core Product
  if (stage === 'review') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-4">Core Product Definition & Pricing</h2>

                <p className="text-muted mb-4">
                  Let's review your core product from Business Basics:
                </p>

                {error && <Alert variant="danger">{error}</Alert>}

                {!isEditingProduct ? (
                  <>
                    <Card className="mb-4 border-info">
                      <Card.Body>
                        <h5>Product Name:</h5>
                        <p className="mb-3">{productName}</p>
                        <h5>Brief Description:</h5>
                        <p className="mb-0">{productDescription}</p>
                      </Card.Body>
                    </Card>

                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setIsEditingProduct(true)}
                      >
                        Edit Product Details
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleContinueFromReview}
                      >
                        Continue
                      </Button>
                    </div>
                  </>
                ) : (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Product Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={productName}
                        onChange={(e) => setProductName(e.target.value)}
                        placeholder="Enter product name"
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label>Brief Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={4}
                        value={productDescription}
                        onChange={(e) => setProductDescription(e.target.value)}
                        placeholder="Describe your core product or service"
                      />
                    </Form.Group>

                    <div className="d-flex justify-content-between">
                      <Button
                        variant="outline-secondary"
                        onClick={() => setIsEditingProduct(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="primary"
                        onClick={handleProductEdit}
                        disabled={saving}
                      >
                        {saving ? 'Saving...' : 'Save & Continue'}
                      </Button>
                    </div>
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // STAGE 2: Pricing Model Definition
  if (stage === 'pricing_model') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-2">💰 Define Your Pricing</h2>
                {productName && (
                  <p className="text-primary fw-semibold mb-4" style={{ fontSize: '1.1rem' }}>
                    Product: {productName}
                  </p>
                )}

                <p className="text-muted mb-4">
                  Is your product recurring (subscription) or one-time purchase?
                </p>

                {showReassurance && (
                  <Alert variant="info" className="d-flex justify-content-between align-items-start">
                    <div>
                      <strong>💡 Don't overthink it!</strong>
                      <p className="mb-0 small">Pick what feels right - you can always change this later. Your reports will show what's working.</p>
                    </div>
                    <button
                      type="button"
                      className="btn-close btn-close-white ms-2"
                      aria-label="Dismiss"
                      onClick={dismissReassurance}
                      style={{ fontSize: '0.75rem' }}
                    />
                  </Alert>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                <Form.Group className="mb-4">
                  <Form.Check
                    type="radio"
                    id="model-monthly"
                    name="pricingModel"
                    label="Monthly subscription"
                    checked={pricingModel === 'monthly'}
                    onChange={() => setPricingModel('monthly')}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="model-annual"
                    name="pricingModel"
                    label="Annual subscription"
                    checked={pricingModel === 'annual'}
                    onChange={() => setPricingModel('annual')}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="model-quarterly"
                    name="pricingModel"
                    label="Quarterly subscription"
                    checked={pricingModel === 'quarterly'}
                    onChange={() => setPricingModel('quarterly')}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="model-onetime"
                    name="pricingModel"
                    label="One-time purchase"
                    checked={pricingModel === 'one-time'}
                    onChange={() => setPricingModel('one-time')}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="model-hybrid"
                    name="pricingModel"
                    label="Hybrid (one-time + recurring)"
                    checked={pricingModel === 'hybrid'}
                    onChange={() => setPricingModel('hybrid')}
                    className="mb-3"
                  />
                  <Form.Check
                    type="radio"
                    id="model-hourly"
                    name="pricingModel"
                    label="Hourly / Project-based"
                    checked={pricingModel === 'hourly'}
                    onChange={() => setPricingModel('hourly')}
                  />
                </Form.Group>

                <div className="d-flex justify-content-between">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('review')}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handlePricingModelSelect}
                  >
                    Continue
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  // STAGE 3: Price Input
  if (stage === 'price_input') {
    return (
      <Container className="py-5">
        <BackToDashboard module="business-model" />
        <Row className="justify-content-center">
          <Col md={10} lg={8}>
            <Card className="shadow">
              <Card.Body className="p-4">
                <h2 className="mb-2">Enter Your Pricing</h2>
                {productName && (
                  <p className="text-primary fw-semibold mb-4" style={{ fontSize: '1.1rem' }}>
                    Product: {productName}
                  </p>
                )}

                {error && <Alert variant="danger">{error}</Alert>}

                {/* One-time pricing */}
                {pricingModel === 'one-time' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>One-time price</Form.Label>
                      <Form.Control
                        type="number"
                        value={oneTimePrice}
                        onChange={(e) => setOneTimePrice(e.target.value)}
                        placeholder="997"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Cost to deliver</Form.Label>
                      <Form.Control
                        type="number"
                        value={costToDeliver}
                        onChange={(e) => setCostToDeliver(e.target.value)}
                        placeholder="50"
                      />
                    </Form.Group>

                    <Alert variant="info">
                      <strong>Profit per sale:</strong> ${calculateProfit()}
                    </Alert>
                  </>
                )}

                {/* Monthly/Annual/Quarterly pricing */}
                {['monthly', 'annual', 'quarterly'].includes(pricingModel) && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>
                        {pricingModel === 'monthly' ? 'Monthly' :
                         pricingModel === 'annual' ? 'Annual' : 'Quarterly'} price
                      </Form.Label>
                      <Form.Control
                        type="number"
                        value={pricingModel === 'monthly' ? monthlyPrice :
                               pricingModel === 'annual' ? annualPrice : quarterlyPrice}
                        onChange={(e) => {
                          if (pricingModel === 'monthly') setMonthlyPrice(e.target.value);
                          else if (pricingModel === 'annual') setAnnualPrice(e.target.value);
                          else setQuarterlyPrice(e.target.value);
                        }}
                        placeholder={pricingModel === 'monthly' ? '97' : pricingModel === 'annual' ? '970' : '270'}
                      />
                    </Form.Group>

                    <p className="mb-3">Do you offer multiple billing options?</p>

                    {pricingModel === 'monthly' && (
                      <>
                        <Form.Check
                          type="checkbox"
                          id="offer-annual"
                          label="Also offer annual"
                          checked={offerAnnual}
                          onChange={(e) => setOfferAnnual(e.target.checked)}
                          className="mb-2"
                        />
                        {offerAnnual && (
                          <Form.Group className="mb-3 ms-4">
                            <Form.Label>Annual discount %</Form.Label>
                            <Form.Control
                              type="number"
                              value={annualDiscount}
                              onChange={(e) => setAnnualDiscount(e.target.value)}
                              placeholder="20"
                              style={{ width: '150px' }}
                            />
                          </Form.Group>
                        )}

                        <Form.Check
                          type="checkbox"
                          id="offer-quarterly"
                          label="Also offer quarterly"
                          checked={offerQuarterly}
                          onChange={(e) => setOfferQuarterly(e.target.checked)}
                          className="mb-2"
                        />
                        {offerQuarterly && (
                          <Form.Group className="mb-3 ms-4">
                            <Form.Label>Quarterly discount %</Form.Label>
                            <Form.Control
                              type="number"
                              value={quarterlyDiscount}
                              onChange={(e) => setQuarterlyDiscount(e.target.value)}
                              placeholder="10"
                              style={{ width: '150px' }}
                            />
                          </Form.Group>
                        )}
                      </>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Cost to deliver per customer/month</Form.Label>
                      <Form.Control
                        type="number"
                        value={costToDeliver}
                        onChange={(e) => setCostToDeliver(e.target.value)}
                        placeholder="10"
                      />
                    </Form.Group>

                    <Alert variant="info">
                      <strong>Profit per customer/month:</strong> ${calculateProfit()}
                    </Alert>
                  </>
                )}

                {/* Hybrid pricing */}
                {pricingModel === 'hybrid' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>One-time component</Form.Label>
                      <Form.Control
                        type="number"
                        value={hybridOneTime}
                        onChange={(e) => setHybridOneTime(e.target.value)}
                        placeholder="497"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Monthly recurring</Form.Label>
                      <Form.Control
                        type="number"
                        value={hybridMonthly}
                        onChange={(e) => setHybridMonthly(e.target.value)}
                        placeholder="97"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Cost to deliver (one-time)</Form.Label>
                      <Form.Control
                        type="number"
                        value={hybridOneTimeCost}
                        onChange={(e) => setHybridOneTimeCost(e.target.value)}
                        placeholder="50"
                      />
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label>Cost to deliver (monthly)</Form.Label>
                      <Form.Control
                        type="number"
                        value={hybridMonthlyCost}
                        onChange={(e) => setHybridMonthlyCost(e.target.value)}
                        placeholder="10"
                      />
                    </Form.Group>

                    <Alert variant="info">
                      <strong>First year revenue per customer:</strong> ${calculateProfit()}
                    </Alert>
                  </>
                )}

                {/* Hourly pricing */}
                {pricingModel === 'hourly' && (
                  <>
                    <Form.Group className="mb-3">
                      <Form.Label>Hourly rate</Form.Label>
                      <Form.Control
                        type="number"
                        value={hourlyRate}
                        onChange={(e) => setHourlyRate(e.target.value)}
                        placeholder="250"
                      />
                    </Form.Group>

                    <Form.Check
                      type="checkbox"
                      id="hours-vary"
                      label="Project hours vary significantly (skip typical hours)"
                      checked={hoursVarySignificantly}
                      onChange={(e) => setHoursVarySignificantly(e.target.checked)}
                      className="mb-3"
                    />

                    {!hoursVarySignificantly && (
                      <Row className="mb-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Typical hours (minimum)</Form.Label>
                            <Form.Control
                              type="number"
                              value={typicalHoursMin}
                              onChange={(e) => setTypicalHoursMin(e.target.value)}
                              placeholder="5"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label>Typical hours (maximum)</Form.Label>
                            <Form.Control
                              type="number"
                              value={typicalHoursMax}
                              onChange={(e) => setTypicalHoursMax(e.target.value)}
                              placeholder="20"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    )}

                    <Form.Group className="mb-3">
                      <Form.Label>Cost per hour (optional)</Form.Label>
                      <Form.Control
                        type="number"
                        value={hourlyCostPerHour}
                        onChange={(e) => setHourlyCostPerHour(e.target.value)}
                        placeholder="0"
                      />
                      <Form.Text className="text-muted">
                        Include overhead, software, support staff costs
                      </Form.Text>
                    </Form.Group>

                    <Alert variant="info">
                      <strong>Profit per hour:</strong> ${calculateProfit()}
                      {!hoursVarySignificantly && calculateTypicalProjectValue() && (
                        <>
                          <br />
                          <strong>Typical project value:</strong> ${calculateTypicalProjectValue().min} - ${calculateTypicalProjectValue().max}
                          <br />
                          <small className="text-muted">Average: ${calculateTypicalProjectValue().avg}</small>
                        </>
                      )}
                    </Alert>
                  </>
                )}

                <div className="d-flex justify-content-between mt-4">
                  <Button
                    variant="outline-secondary"
                    onClick={() => setStage('pricing_model')}
                  >
                    Back
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleSavePricing}
                    disabled={saving}
                  >
                    {saving ? 'Saving...' : 'Save & Continue'}
                  </Button>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    );
  }

  return null;
}

export default CoreProductPricing;
